import React from 'react';
import { Star, Award, Users, Heart, ExternalLink } from 'lucide-react';

const aboutText = `Kolinsky Art Gallery is a premier destination for art enthusiasts on Sri Lanka's southern coast. Established with a passion for showcasing exceptional artworks, elegant ornaments, and refined home décor, we take pride in curating collections that inspire and delight.

Our galleries in Unawatuna and Mirissa provide a serene space to discover unique pieces that celebrate creativity and craftsmanship from talented local artists. We believe art has the power to transform spaces and enrich lives.`;

const reviews = [
  {
    name: "Paul Simpson",
    text: "I highly recommend Kolinsky Gallery in Unwatuna. I have been dealing with Isuru who has provided a brilliant service. If you are looking for artwork and a top class service Kolinsky should be your destination. Highly recommended.",
    rating: 5
  },
  {
    name: "Saloua El Chimi",
    text: "This is the oldest Art shop in Unawatuna and the paintings they have are beautiful! The shop is great with so many choices, and the Nuwan that sells the paintings is very helpful. Prices are reasonable and fair. Its just a pity that other galleries copy the paintings they have, so I really recommend you getting an original one at Kolinsky Art Gallery, supporting these amazing artist and having a great souvenir from Sri Lanka, I just love everything I have bought there. Definitely worth checking out",
    rating: 5
  },
  {
    name: "Carmen Garcia Pedraz",
    text: "Im from spain and I bought two art pieces that are AMAZING! The owner treated me so good and gave me only facilities, making the experience much better! The worst part was deciding the two that i wanted because all of them were beautiful.",
    rating: 5
  }
];

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5 sm:gap-1">
    {Array(rating).fill(0).map((_, i) => (
      <Star key={i} className="w-3 sm:w-4 h-3 sm:h-4 fill-yellow-400 text-yellow-400" />
    ))}
  </div>
);

const ReviewCard = ({ review }) => (
  <div className="bg-gradient-to-br from-orange-50 to-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md border border-orange-100 hover:shadow-lg transition flex flex-col h-full">
    <div className="mb-3 sm:mb-4">
      <StarRating rating={review.rating} />
    </div>
    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3 sm:mb-4 flex-grow">
      "{review.text}"
    </p>
    <p className="font-semibold text-xs sm:text-sm text-gray-800 mt-auto">
      – {review.name}
    </p>
  </div>
);

const AboutPage = () => {
    return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[35vh] sm:h-[40vh] md:h-[45vh] min-h-[280px] flex items-center justify-center overflow-hidden bg-gray-800">
        <div className="absolute inset-0">
          <img
            src="https://kolinskyartstorage.blob.core.windows.net/$web/hero-about.jpg"
            alt="Kolinsky Art Gallery space or team"
            className="w-full h-full object-cover opacity-70"
            onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/1920x600/gray/white?text=Error"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-2 sm:mb-4 drop-shadow-lg">About Us</h1>
          <p className="text-sm sm:text-lg md:text-2xl font-light drop-shadow-md">Celebrating Art & Creativity</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        
        {/* Story */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-5 sm:p-8 md:p-12 mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-gray-800 mb-4 sm:mb-6">Our Story</h2>
          <p className="text-xs sm:text-base md:text-lg text-gray-600 leading-relaxed whitespace-pre-line">
            {aboutText}
          </p>
        </div>

        {/* Mission & Values */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
          {/* Value 1 */}
          <div className="bg-white rounded-lg sm:rounded-xl p-5 sm:p-6 shadow-md border border-gray-100 text-center transition hover:shadow-lg">
            <div className="w-12 sm:w-16 h-12 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Award className="w-6 sm:w-8 h-6 sm:h-8 text-orange-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base md:text-lg text-gray-800 mb-2">Curated Excellence</h3>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
              Every piece is carefully selected for its quality and artistic merit.
            </p>
          </div>

          {/* Value 2 */}
          <div className="bg-white rounded-lg sm:rounded-xl p-5 sm:p-6 shadow-md border border-gray-100 text-center transition hover:shadow-lg">
            <div className="w-12 sm:w-16 h-12 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Users className="w-6 sm:w-8 h-6 sm:h-8 text-orange-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base md:text-lg text-gray-800 mb-2">Artist Support</h3>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
              We champion local artists, providing them a platform to shine.
            </p>
          </div>

          {/* Value 3 */}
          <div className="bg-white rounded-lg sm:rounded-xl p-5 sm:p-6 shadow-md border border-gray-100 text-center transition hover:shadow-lg">
            <div className="w-12 sm:w-16 h-12 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Heart className="w-6 sm:w-8 h-6 sm:h-8 text-orange-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base md:text-lg text-gray-800 mb-2">Passion for Art</h3>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
              Sharing our love for creativity and enduring beauty with the community.
            </p>
          </div>
        </div>

        {/* Customer Reviews */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-5 sm:p-8 md:p-12">
          <div className="mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-gray-800 mb-3 sm:mb-4">What Our Valued Customers Say</h2>
            <div className="flex gap-0.5 sm:gap-1 text-yellow-400">
              <Star className="w-4 sm:w-5 h-4 sm:h-5 fill-current" />
              <Star className="w-4 sm:w-5 h-4 sm:h-5 fill-current" />
              <Star className="w-4 sm:w-5 h-4 sm:h-5 fill-current" />
              <Star className="w-4 sm:w-5 h-4 sm:h-5 fill-current" />
              <Star className="w-4 sm:w-5 h-4 sm:h-5 fill-current" />
            </div>
          </div>

          {/* Review Cards Grid - FIXED: Removed line-clamp-4 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {reviews.map((review, index) => (
              <ReviewCard key={index} review={review} />
            ))}
          </div>

          {/* See More Reviews Button */}
          <div className="flex justify-center">
            <a
              href="https://share.google/HaEk9zXQd7Ut3l3Mm"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-base font-medium rounded-lg transition-colors shadow-md"
            >
              See More Reviews
              <ExternalLink className="w-3 sm:w-4 h-3 sm:h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;